// The fixed card shown when hovering a citation number in the chat — the
// same shape LeadFlagPopover draws over flagged transcript segments, so a
// reader sees where a [n] lands before clicking: the source, the moment or
// page, and the passage the model cited.
import { DocumentTextIcon, PhoneIcon, TableCellsIcon, UserIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { ChatCitation, formatMs } from "./api";

export type CitationHover = { c: ChatCitation; x: number; y: number; below: boolean };

const KIND_ICON: Record<ChatCitation["kind"], React.FC<{ className?: string }>> = {
  call_segment: PhoneIcon,
  document_page: DocumentTextIcon,
  table_rows: TableCellsIcon,
  entity: UserIcon,
  video_segment: VideoCameraIcon,
};

const KIND_LABEL: Record<ChatCitation["kind"], string> = {
  call_segment: "Call",
  document_page: "Document",
  table_rows: "Spreadsheet",
  entity: "Person",
  video_segment: "Footage",
};

function action(c: ChatCitation): string | null {
  if (c.kind === "video_segment") return c.startMs != null ? `Click to open the footage at ${formatMs(c.startMs)}` : "Click to open the footage";
  if (c.callId) return `Click to play from ${formatMs(c.startMs || 0)}`;
  if (c.fileId) return c.page ? `Click to open page ${c.page}` : "Click to open the file";
  return null;
}

// Citations saved before the server named speakers still carry raw
// diarization labels; read them as "Speaker N" rather than "ch_1".
const tidySpeakers = (text: string) => text.replace(/\b(ch|spk)_(\d+)\b/g, (_m, _k, idx) => `Speaker ${Number(idx) + 1}`);

// Same palette and order as the transcript view (dots keyed by first
// appearance), so a speaker's colour matches when the reader clicks through.
const SPEAKER_COLORS = ["bg-blue-500", "bg-green-500", "bg-purple-500", "bg-orange-500", "bg-red-500", "bg-pink-500"];

// The registry stores call snippets as the chunk text: one "Name: words"
// line per turn, cut at 240 characters. Split it back into turns so the
// card reads like the transcript rather than a run-on quote. Anything that
// doesn't parse (document passages, spreadsheet summaries) renders as prose.
type Turn = { speaker: string; text: string };
const SNIPPET_LIMIT = 240;
function parseTurns(snippet: string): Turn[] | null {
  const lines = snippet.split("\n").map((l) => l.trim()).filter(Boolean);
  const cut = snippet.length >= SNIPPET_LIMIT;
  const turns: Turn[] = [];
  let notDialogue = false;
  lines.forEach((line, i) => {
    const m = line.match(/^([^:\n]{1,48}):\s*(.*)$/);
    if (m) turns.push({ speaker: m[1].trim(), text: m[2] });
    // A line with no colon: the 240-char cut usually landed inside the next
    // speaker's name ("Speake"), so drop that trailing fragment (the
    // ellipsis covers it); anywhere else fold it into the previous turn. A
    // first line with no speaker means this isn't dialogue.
    else if (turns.length && cut && i === lines.length - 1) return;
    else if (turns.length) turns[turns.length - 1].text += ` ${line}`;
    else notDialogue = true;
  });
  return turns.length && !notDialogue ? turns : null;
}

// The card stays while the pointer is on it (so a passage can be selected
// and copied); the parent closes it when the pointer leaves both the number
// and the card.
const CitationPopover: React.FC<{ hover: CitationHover; onMouseEnter?: () => void; onMouseLeave?: () => void }> = ({ hover, onMouseEnter, onMouseLeave }) => {
  const { c } = hover;
  const Icon = KIND_ICON[c.kind] || DocumentTextIcon;
  const corner = c.startMs != null ? formatMs(c.startMs) : c.page ? `p. ${c.page}` : null;
  const hint = action(c);
  return (
    <div
      className="fixed z-50 w-96 rounded-xl border border-base-300 bg-base-100 shadow-xl p-3 select-text cursor-auto"
      style={{ left: hover.x, top: hover.y, transform: hover.below ? undefined : "translateY(-100%)" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-md text-[10px] font-semibold tabular-nums bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300">
          {c.n}
        </span>
        <Icon className="size-3.5 text-base-content/50 shrink-0" />
        <span className="text-xs font-semibold text-base-content truncate">
          <span className="text-base-content/50 font-medium">{KIND_LABEL[c.kind] || "Source"} · </span>
          {c.label.replace(/ @ .*$/, "")}
        </span>
        {corner && <span className="ml-auto shrink-0 text-[11px] tabular-nums text-base-content/50">{corner}</span>}
      </div>
      {c.snippet && <Snippet text={tidySpeakers(c.snippet)} isCall={c.kind === "call_segment"} />}
      {hint && <p className="mt-1.5 text-[11px] text-base-content/50">{hint}</p>}
    </div>
  );
};

const Snippet: React.FC<{ text: string; isCall: boolean }> = ({ text, isCall }) => {
  const turns = isCall ? parseTurns(text) : null;
  // The snippet is a hard cut, so a trailing fragment is expected; say so.
  const cut = text.length >= SNIPPET_LIMIT;
  if (!turns) {
    return (
      <p className="mt-1.5 text-sm italic text-base-content/90 leading-5 line-clamp-5">
        "{text}
        {cut ? "…" : ""}"
      </p>
    );
  }
  const order: string[] = [];
  for (const t of turns) if (!order.includes(t.speaker)) order.push(t.speaker);
  return (
    <div className="mt-2 space-y-1 max-h-40 overflow-hidden">
      {turns.map((t, i) => (
        <div key={i} className="flex gap-2">
          <span className={cn("size-2.5 rounded-full shrink-0 mt-[5px]", SPEAKER_COLORS[order.indexOf(t.speaker) % SPEAKER_COLORS.length])} />
          <p className="min-w-0 text-sm leading-5 text-base-content">
            <span className="text-xs font-semibold text-base-content/70 mr-1">{t.speaker}:</span>
            {t.text}
            {cut && i === turns.length - 1 ? "…" : ""}
          </p>
        </div>
      ))}
    </div>
  );
};

export default CitationPopover;
